import { useState, useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for data fetching with loading states, error handling, and demo data fallback
 * @param {Function} fetchFunction - The API function to call
 * @param {Array} demoData - Fallback demo data if API fails
 * @param {Object} options - Additional options
 * @param {boolean} options.showErrorToast - Whether to show error toast (default: true)
 * @param {string} options.errorMessage - Custom error message
 * @param {Function} options.onSuccess - Callback function on successful data fetch
 * @param {Function} options.onError - Callback function on error
 * @param {Array} options.dependencies - Dependencies for useEffect
 * @returns {Object} - { data, isLoading, error, refetch }
 */
const useDataFetching = (
  fetchFunction,
  demoData = [],
  options = {}
) => {
  const {
    showErrorToast = true,
    errorMessage = 'Failed to load data. Using demo data.',
    onSuccess,
    onError,
    dependencies = [],
    autoFetch = true
  } = options;

  const [data, setData] = useState(demoData);
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [error, setError] = useState(null);



  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetchFunction();
      
      // Handle standardized API response structure
      let responseData;
      
      if (response && typeof response === 'object') {
        // Check if it's our standardized response format
        if (response.hasOwnProperty('data') && response.hasOwnProperty('error')) {
          if (response.error) {
            throw new Error(response.message || 'API returned an error');
          }
          responseData = response.data;
        } else {
          // Fallback for legacy response formats
          responseData = response?.data || response?.doctors || response?.bookings || response?.clinics || response?.result?.docs || response?.result?.doctors || response?.result || response;
        }
      } else {
        responseData = response;
      }
      
      // Only use demo data if there's an actual error or no response at all
      // Don't fall back to demo data for empty API responses
      if (responseData === null || responseData === undefined) {
        responseData = demoData;
      }
      
      setData(responseData);
      
      if (onSuccess) {
        onSuccess(responseData);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error);
      setData(demoData);
      
      if (showErrorToast) {
        toast.error(errorMessage);
      }
      
      if (onError) {
        onError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchData();
    }
  }, dependencies);

  const refetch = () => {
    fetchData();
  };

  return {
    data,
    isLoading,
    error,
    refetch,
    setData
  };
};

export default useDataFetching;
